import { useState, useEffect } from 'react';
import { NavigationMenu } from '../components/NavigationMenu';
import { Button } from '../components/Button';
import { 
  User, 
  Mail, 
  Palette, 
  Sun, 
  Moon, 
  LogOut, 
  AlertTriangle, 
  X, 
  Crown,
  Users,
  Plus,
  Check,
  UserX,
  Gift,
  Copy,

  History,
  Send,
  Trash2,
  Edit,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useTheme } from '../context/ThemeContext';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import axios from 'axios';
import { API_URL } from '../config/api';
import { referralApi, referralUtils, ReferralTransaction } from '../services/referralApi';
import { accountApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { useSubscription, subscriptionQueryKey } from '../hooks/useSubscription';
import { useReferral } from '../hooks/useReferral';
import { useQueryClient } from '@tanstack/react-query';
import { useCacheSync } from '../hooks/useCacheSync';
 

interface TeamMember {
  userId: string;
  nickname: string;
  joinedAt: string;
}



function Profile() {
  useDocumentTitle('Профиль');
  const { profile, logout, token, updateProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useCustomNavigate();
  // Флаг для полного скрытия реферального блока из DOM, чтобы не влиял на отступы space-y
  const showReferral = false;
  
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Используем хуки для кеширования данных
  const queryClient = useQueryClient();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const { data: referralInfoFromCache, isLoading: referralLoading } = useReferral();
  
  // Синхронизация кеша через WebSocket
  useCacheSync();
  
  const loading = subscriptionLoading || referralLoading;
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [newMemberNickname, setNewMemberNickname] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [userExists, setUserExists] = useState<boolean | null>(null);
  const [checkingUser, setCheckingUser] = useState(false);
  
  // Состояние для реферальной системы
  const referralInfo = referralInfoFromCache;
  const [referralHistory, setReferralHistory] = useState<ReferralTransaction[]>([]);
  const [showReferralHistory, setShowReferralHistory] = useState(false);
  const [loadingReferralHistory, setLoadingReferralHistory] = useState(false);
  const [showProfessionalModal, setShowProfessionalModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [professionalForm, setProfessionalForm] = useState({ phoneNumber: '', message: '' });
  const [withdrawalForm, setWithdrawalForm] = useState({ amount: 3000 });
  const [copiedLink, setCopiedLink] = useState(false);
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);

  // Состояние для удаления участников команды
  const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [memberRemovalError, setMemberRemovalError] = useState<string | null>(null);
  const [memberWithError, setMemberWithError] = useState<string | null>(null);

  // Состояние для смены никнейма и пароля
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [nicknameExists, setNicknameExists] = useState<boolean | null>(null);
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [savingNickname, setSavingNickname] = useState(false);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [addMemberError, setAddMemberError] = useState<string | null>(null);
  const [professionalApplicationError, setProfessionalApplicationError] = useState<string | null>(null);
  const [professionalApplicationSuccess, setProfessionalApplicationSuccess] = useState(false);
  const [withdrawalApplicationError, setWithdrawalApplicationError] = useState<string | null>(null);
  const [withdrawalApplicationSuccess, setWithdrawalApplicationSuccess] = useState(false);

  // Состояния для уведомлений о платежах
  const [paymentNotification, setPaymentNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Обработка результатов платежа
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      setPaymentNotification({
        type: 'success',
        message: 'Подписка успешно оформлена! Обновляем информацию...'
      });
      
      // Обновляем данные подписки
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKey(token!) });
      
      // Убираем уведомление через 5 секунд
      setTimeout(() => {
        setPaymentNotification(null);
      }, 5000);
      
      // Очищаем URL
      window.history.replaceState({}, '', '/profile');
    } else if (paymentStatus === 'failed') {
      setPaymentNotification({
        type: 'error',
        message: 'Ошибка при оформлении подписки. Попробуйте еще раз.'
      });
      
      // Убираем уведомление через 10 секунд
      setTimeout(() => {
        setPaymentNotification(null);
      }, 10000);
      
      // Очищаем URL
      window.history.replaceState({}, '', '/profile');
    }
  }, [token, queryClient]);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Ошибка при выходе:', error);
    } finally {
      setShowLogoutDialog(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  const handleAddMember = async () => {
    if (!newMemberNickname.trim() || !subscription?.team || !token || !userExists) return;

    setAddingMember(true);
    setAddMemberError(null);
    
    try {
      await axios.post(`${API_URL}/teams/${subscription.team.teamId}/members`, {
        userId: newMemberNickname.trim()
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Обновляем данные подписки
      await axios.get(`${API_URL}/usersubscription/current`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKey(token!) });
      
      setNewMemberNickname('');
      setUserExists(null);
      setShowAddMemberDialog(false);
      setAddMemberError(null);
    } catch (error) {
      console.error('Ошибка при добавлении участника:', error);
      setAddMemberError('Ошибка при добавлении участника команды');
    } finally {
      setAddingMember(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getSubscriptionStatusColor = (daysRemaining: number) => {
    if (daysRemaining <= 3) return 'text-red-600 dark:text-red-400';
    if (daysRemaining <= 7) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const checkUserExists = async (nickname: string) => {
    if (!nickname.trim() || nickname.length < 2) {
      setUserExists(null);
      return;
    }

    setCheckingUser(true);
    try {
      const response = await axios.get(`${API_URL}/account/search-users?query=${encodeURIComponent(nickname)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUserExists(response.data.success);
    } catch (error) {
      console.error('Ошибка при проверке пользователя:', error);
      setUserExists(false);
    } finally {
      setCheckingUser(false);
    }
  };

  // Функции для реферальной системы
  const handleCopyReferralLink = async () => {
    if (!referralInfo) return;
    
    const success = await referralUtils.copyReferralLink(referralInfo.referralLink);
    if (success) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
    // Убираем алерт - пользователь увидит что ссылка не скопировалась по отсутствию галочки
  };

  const handleShowHistory = async () => {
    // Сначала открываем модальное окно
    setShowReferralHistory(true);
    setLoadingReferralHistory(true);
    
    // Затем загружаем данные в фоне
    try {
      const history = await referralApi.getReferralHistory();
      setReferralHistory(history);
    } catch (error) {
      console.error('Ошибка при загрузке истории:', error);
      setReferralHistory([]);
      // Можно показать уведомление об ошибке, но модальное окно остается открытым
    } finally {
      setLoadingReferralHistory(false);
    }
  };

  const handleProfessionalApplication = async () => {
    setProfessionalApplicationError(null);
    setProfessionalApplicationSuccess(false);
    
    if (!dataProcessingConsent) {
      setProfessionalApplicationError('Необходимо согласие на обработку персональных данных');
      return;
    }
    
    try {
      await referralApi.applyForProfessionalProgram(professionalForm);
      setShowProfessionalModal(false);
      setProfessionalForm({ phoneNumber: '', message: '' });
      setDataProcessingConsent(false);
      setProfessionalApplicationSuccess(true);
      setProfessionalApplicationError(null);
      
      // Убираем сообщение об успехе через 5 секунд
      setTimeout(() => {
        setProfessionalApplicationSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Ошибка при отправке заявки:', error);
      setProfessionalApplicationError('Ошибка при отправке заявки');
    }
  };

  const handleWithdrawal = async () => {
    setWithdrawalApplicationError(null);
    setWithdrawalApplicationSuccess(false);
    
    try {
      await referralApi.createWithdrawalRequest({ amount: withdrawalForm.amount });
      setShowWithdrawalModal(false);
      setWithdrawalForm({ amount: 3000 });
      setWithdrawalApplicationSuccess(true);
      setWithdrawalApplicationError(null);
      
      // Убираем сообщение об успехе через 5 секунд
      setTimeout(() => {
        setWithdrawalApplicationSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Ошибка при создании заявки на вывод:', error);
      setWithdrawalApplicationError('Ошибка при создании заявки на вывод');
    }
  };

  // Функции для управления участниками команды
  const canRemoveMember = (member: TeamMember): boolean => {
    const joinedDate = new Date(member.joinedAt);
    const now = new Date();
    const daysSinceJoined = Math.floor((now.getTime() - joinedDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceJoined >= 14;
  };

  const getDaysUntilRemovalAllowed = (member: TeamMember): number => {
    const joinedDate = new Date(member.joinedAt);
    const now = new Date();
    const daysSinceJoined = Math.floor((now.getTime() - joinedDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, 14 - daysSinceJoined);
  };

  // Функции для смены никнейма
  const handleEditNickname = () => {
    setNewNickname(profile?.nickname || '');
    setIsEditingNickname(true);
    setNicknameExists(null);
  };

  const checkNicknameExists = async (nickname: string) => {
    if (!nickname.trim() || nickname.length < 2 || nickname === profile?.nickname) {
      setNicknameExists(null);
      return;
    }

    setCheckingNickname(true);
    try {
      const response = await accountApi.searchUsers(nickname);
      setNicknameExists(response.success);
    } catch (error) {
      console.error('Ошибка при проверке никнейма:', error);
      setNicknameExists(false);
    } finally {
      setCheckingNickname(false);
    }
  };

  const handleSaveNickname = async () => {
    if (!newNickname.trim() || newNickname === profile?.nickname || nicknameExists) return;

    setSavingNickname(true);
    setNicknameError(null);
    
    try {
      const response = await accountApi.changeNickname(newNickname);
      console.log('API Response:', response); // Для отладки
      
      // Если дошли до этой точки, значит запрос был успешен (статус 200)
      // API возвращает { Success: true, NewNickname: "новый_ник" }
      if (response && (response.Success === true || response.success === true)) {
        const updatedNickname = response.NewNickname || response.newNickname || newNickname;
        
        // Обновляем профиль через AuthContext
        updateProfile({ nickname: updatedNickname });
        
        setIsEditingNickname(false);
        setNewNickname('');
        setNicknameExists(null);
        setNicknameError(null);
      } else {
        // API вернул Success: false
        const errorMessage = response?.Message || response?.message || 'Неизвестная ошибка при смене никнейма';
        setNicknameError(errorMessage);
      }
    } catch (error: any) {
      console.error('Ошибка при смене никнейма:', error);
      // Ошибка может быть из-за неправильного статуса HTTP или проблем с сетью
      setNicknameError(error.message || 'Ошибка при смене никнейма');
    } finally {
      setSavingNickname(false);
    }
  };

  const handleCancelNickname = () => {
    setIsEditingNickname(false);
    setNewNickname('');
    setNicknameExists(null);
    setNicknameError(null);
  };

  // Функции для смены пароля
  const handleChangePasswordClick = () => {
    setIsChangingPassword(true);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  const handleSavePassword = async () => {
    setPasswordError(null);
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('Заполните все поля');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Новый пароль должен быть не менее 6 символов');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await accountApi.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      console.log('Password API Response:', response); // Для отладки
      
      // Если дошли до этой точки, значит запрос был успешен (статус 200)
      if (response && (response.Success === true || response.success === true)) {
        setIsChangingPassword(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setPasswordError(null);
      } else {
        // API вернул Success: false
        const errorMessage = response?.Message || response?.message || 'Неизвестная ошибка при смене пароля';
        setPasswordError(errorMessage);
      }
    } catch (error: any) {
      console.error('Ошибка при смене пароля:', error);
      // Ошибка может быть из-за неправильного статуса HTTP или проблем с сетью
      setPasswordError(error.message || 'Ошибка при смене пароля');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleRemoveMemberClick = (member: TeamMember) => {
    // Очищаем предыдущие ошибки
    setMemberRemovalError(null);
    setMemberWithError(null);
    
    if (!canRemoveMember(member)) {
      const daysRemaining = getDaysUntilRemovalAllowed(member);
      setMemberRemovalError(`Можно удалить только через ${daysRemaining} дней`);
      setMemberWithError(member.userId);
      return;
    }

    setMemberToRemove(member);
    setShowRemoveMemberDialog(true);
  };

  const handleRemoveMemberConfirm = async () => {
    if (!memberToRemove || !subscription?.team || !token) return;

    setRemovingMember(true);
    try {
      await axios.delete(`${API_URL}/teams/${subscription.team.teamId}/members/${memberToRemove.userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Обновляем данные подписки
      await axios.get(`${API_URL}/usersubscription/current`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKey(token!) });
      
      setShowRemoveMemberDialog(false);
      setMemberToRemove(null);
      setMemberRemovalError(null);
      setMemberWithError(null);
    } catch (error: any) {
      console.error('Ошибка при удалении участника:', error);
      if (error.response?.data?.error) {
        setMemberRemovalError(error.response.data.error);
      } else {
        setMemberRemovalError('Ошибка при удалении участника команды');
      }
    } finally {
      setRemovingMember(false);
    }
  };

  if (!profile) {
    return (
      <div className="h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200 mobile-content-padding">
        <div className="hidden md:block">
          <NavigationMenu />
        </div>
        <div className="flex items-center justify-center h-[calc(100vh-96px)]">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-y-auto">
      <div className="hidden md:block">
        <NavigationMenu />
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Профиль"
          subtitle="Управляйте своим аккаунтом и подпиской"
        />

        {/* Уведомления о платежах */}
        {paymentNotification && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            paymentNotification.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
          }`}>
            <Check className={`w-5 h-5 mr-3 ${
              paymentNotification.type === 'success' ? 'text-green-500' : 'text-red-500'
            }`} />
            <p>{paymentNotification.message}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Личная информация */}
          <div className="space-y-6 lg:order-2">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Личная информация
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Никнейм
                    </div>
                    {isEditingNickname ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={newNickname}
                            onChange={(e) => {
                              setNewNickname(e.target.value);
                              checkNicknameExists(e.target.value);
                            }}
                            className={`w-full px-2 py-1 border rounded text-lg font-medium dark:bg-gray-700 dark:text-white ${
                              checkingNickname
                                ? 'border-blue-300 dark:border-blue-600'
                                : nicknameExists === true 
                                ? 'border-red-300 dark:border-red-600' 
                                : nicknameExists === false 
                                ? 'border-green-300 dark:border-green-600' 
                                : 'border-gray-300 dark:border-gray-600'
                            }`}
                            disabled={savingNickname}
                            autoFocus
                          />
                          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                            {checkingNickname ? (
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            ) : nicknameExists === false ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : nicknameExists === true ? (
                              <UserX className="w-4 h-4 text-red-500" />
                            ) : null}
                          </div>
                        </div>
                        <button
                          onClick={handleSaveNickname}
                          disabled={!newNickname.trim() || newNickname === profile?.nickname || nicknameExists || savingNickname || checkingNickname}
                          className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50 transition-colors"
                          title="Сохранить"
                        >
                          {savingNickname ? (
                            <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={handleCancelNickname}
                          disabled={savingNickname}
                          className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                          title="Отмена"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                          {profile.nickname}
                        </div>
                        <button
                          onClick={handleEditNickname}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Изменить никнейм"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {nicknameExists === true && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        Никнейм уже занят
                      </div>
                    )}
                    {nicknameError && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {nicknameError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Email
                    </div>
                    <div className="text-lg font-medium text-gray-900 dark:text-white">
                      {profile.email}
                    </div>
                  </div>
                </div>

                {/* Блок смены пароля */}
                <div className="border-t dark:border-gray-700 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Пароль
                    </div>
                    {!isChangingPassword && (
                      <button
                        onClick={handleChangePasswordClick}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-600 dark:border-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                      >
                        Сменить пароль
                      </button>
                    )}
                  </div>
                  
                  {isChangingPassword && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Текущий пароль
                        </label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            disabled={savingPassword}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            aria-label="Показать/скрыть текущий пароль"
                          >
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Новый пароль
                        </label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            disabled={savingPassword}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            aria-label="Показать/скрыть новый пароль"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Подтверждение нового пароля
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            disabled={savingPassword}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            aria-label="Показать/скрыть подтверждение пароля"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      {passwordError && (
                        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                          {passwordError}
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSavePassword}
                          disabled={!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || passwordForm.newPassword !== passwordForm.confirmPassword || savingPassword}
                          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-600 dark:border-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                        >
                          {savingPassword ? (
                            <div className="flex items-center">
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                              Сохранение...
                            </div>
                          ) : (
                            'Сохранить'
                          )}
                        </button>
                        <button
                          onClick={handleCancelPassword}
                          disabled={savingPassword}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border border-gray-600 dark:border-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-colors disabled:opacity-50"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* Настройки */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Настройки
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                        <Palette className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Тема интерфейса
                      </div>
                      <div className="text-lg font-medium text-gray-900 dark:text-white">
                        {theme === 'light' ? 'Светлая' : 'Темная'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center text-white shadow-lg"
                    aria-label="Переключить тему"
                  >
                    {theme === 'light' ? (
                      <Moon className="w-6 h-6" />
                    ) : (
                      <Sun className="w-6 h-6" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                        <LogOut className="w-6 h-6 text-red-600 dark:text-red-400" />
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Выход из системы
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogoutClick}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg font-medium"
                    aria-label="Выйти из системы"
                  >
                    Выйти
                  </button>
                </div>
              </div>
            </div>
            

            {/* Подписка */}
            {loading ? (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                </div>
              </div>
            ) : subscription ? (
              <>
                
              </>
            ) : (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Нет активной подписки
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Выберите подходящий тарифный план для начала работы
                </p>
                <Button
                  onClick={() => navigate('/tariffs')}
                  variant="primary-filled"
                >
                  Выбрать тариф
                </Button>
              </div>
            )}
          </div>

          

          {/* Подписка и реферальная программа */}
          <div className="space-y-6 lg:order-1">
            {/* Реферальная программа (полностью скрыта, не влияет на отступы) */}
            {showReferral && (
              loading ? (
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
                  <div className="animate-pulse space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                      <div className="flex items-center space-x-2">
                        <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded"></div>
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                      </div>
                    </div>
                    {/* Skeleton для баланса */}
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4">
                      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-2"></div>
                      <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                    </div>
                    {/* Skeleton для статистики */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-24 mb-1"></div>
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-8"></div>
                      </div>
                      <div>
                        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-24 mb-1"></div>
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-8"></div>
                      </div>
                    </div>
                    {/* Skeleton для промокода */}
                    <div>
                      <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-1"></div>
                      <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                    </div>
                    {/* Skeleton для ссылки */}
                    <div>
                      <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-28 mb-1"></div>
                      <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                    </div>
                    {/* Skeleton для кнопок */}
                    <div className="space-y-2">
                      <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
                      <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              ) : referralInfo ? (
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Реферальная программа
              </h2>
                  <div className="flex items-center space-x-2">
                    <Gift className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {referralInfo.program === 'Personal' ? 'Личная' : 'Профессиональная'}
                    </span>
                  </div>
                </div>
              
              <div className="space-y-4">
                  {/* Баланс */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Ваш баланс</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {referralInfo.balance.toFixed(2)} ₽
                      </div>
                    </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-green-500">₽</span>
                        <button
                          onClick={handleShowHistory}
                          className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="История операций"
                        >
                          <History className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Статистика и условия */}
                  <div className="border-b dark:border-gray-700 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                        <div className="text-gray-500 dark:text-gray-400">Ваше вознаграждение</div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {referralInfo.program === 'Personal' ? '20%' : '50%'}
                      </div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-400">Скидка для друзей</div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {referralInfo.program === 'Personal' ? '5%' : '15%'}
                    </div>
                  </div>
                    </div>
                  </div>

                  {/* Промокод */}
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Ваш промокод</div>
                    <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-lg font-mono text-sm font-bold text-center relative text-gray-900 dark:text-white">
                      {referralInfo.personalPromoCode}
                  <button
                        onClick={() => {
                          navigator.clipboard.writeText(referralInfo.personalPromoCode);
                          setCopiedLink(true);
                          setTimeout(() => setCopiedLink(false), 2000);
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                        title="Копировать промокод"
                      >
                        {copiedLink ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3 text-gray-500" />
                    )}
                  </button>
                </div>
                      </div>

                  {/* Реферальная ссылка */}
                    <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Ваша реферальная ссылка</div>
                    <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-lg text-xs text-center relative text-gray-900 dark:text-white break-all">
                      {referralInfo.referralLink}
                      <button
                        onClick={handleCopyReferralLink}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                        title="Копировать ссылку"
                      >
                        {copiedLink ? (
                          <Check className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3 text-gray-500" />
                        )}
                      </button>
                      </div>
                    </div>

                  {/* Кнопки действий */}
                  <div className="flex flex-wrap gap-2">
                    {referralInfo.program === 'Personal' && (
                      <div className="w-full">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-2">
                          <p className="text-sm text-blue-800 dark:text-blue-300 leading-relaxed">
                            <strong>Повысьте статус до Профессионального!</strong><br/>
                            Получайте 50% от каждого платежа приглашенных пользователей (вместо 20%) и выводите средства на карту. Статус предоставляется блогерам и медиа-персонам после модерации.
                          </p>
                        </div>
                        
                        {professionalApplicationSuccess && (
                          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-2">
                            <p className="text-sm text-green-800 dark:text-green-300">
                              Заявка отправлена! Мы свяжемся с вами в ближайшее время.
                            </p>
                          </div>
                        )}
                        
                        {professionalApplicationError && (
                          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-2">
                            <p className="text-sm text-red-800 dark:text-red-300">
                              {professionalApplicationError}
                            </p>
                          </div>
                        )}
                        
                        <button
                          onClick={() => setShowProfessionalModal(true)}
                          className="w-full px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-sm font-medium"
                        >
                          Повысить статус
                        </button>
                </div>
                    )}
                    
                    {referralInfo.program === 'Professional' && referralInfo.canWithdraw && referralInfo.balance >= referralInfo.minWithdrawalAmount && (
                      <button
                        onClick={() => setShowWithdrawalModal(true)}
                        className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Вывести средства
                      </button>
                    )}
                    
                    {withdrawalApplicationSuccess && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                        <p className="text-sm text-green-800 dark:text-green-300">
                          Заявка на вывод отправлена! Мы свяжемся с вами для уточнения реквизитов.
                        </p>
                      </div>
                    )}
                    
                    {withdrawalApplicationError && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {withdrawalApplicationError}
                        </p>
                      </div>
                    )}
                  </div>
            </div>
                </div>
              ) : null
            )}

            {/* Информация о подписке */}
            {subscription && (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      Подписка
                    </h2>
                    <div className="flex items-center space-x-2">
                      <Crown className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                        {subscription.isTrial ? 'Пробный период' : subscription.name}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">Действует до</div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatDate(subscription.endDate)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">Осталось дней</div>
                        <div className={`text-sm font-bold ${getSubscriptionStatusColor(subscription.daysRemaining)}`}>
                          {subscription.daysRemaining > 0 ? subscription.daysRemaining : 'Истекла'}
                        </div>
                      </div>
                    </div>

                    {subscription.team && (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm text-gray-500 dark:text-gray-400">Команда</div>
                        </div>
                        
                        <div className="flex items-center space-x-3 mb-3">
                          <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white text-sm">
                              {subscription.team.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {subscription.team.membersCount} / {subscription.teamSize} участников
                            </div>
                          </div>
                        </div>

                        {/* Список участников команды */}
                        {subscription.team.members && subscription.team.members.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {subscription.team.members.map((member: TeamMember) => (
                              <div key={member.userId}>
                              <div 
                                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                      {member.nickname.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {member.nickname}
                                  </span>
                                </div>
                                  <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(member.joinedAt).toLocaleDateString('ru-RU')}
                                </span>
                                    <button
                                      onClick={() => handleRemoveMemberClick(member)}
                                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                                      title="Удалить участника"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                                {memberWithError === member.userId && memberRemovalError && (
                                  <div className="px-2 mt-1">
                                    <div className="text-xs text-red-600 dark:text-red-400">
                                      {memberRemovalError}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Кнопка добавления участника */}
                        {subscription.team.membersCount < subscription.teamSize && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => setShowAddMemberDialog(true)}
                              className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors rounded"
                              title="Добавить участника"
                            >
                              <Plus className="w-5 h-5 mr-2" />
                              Добавить участника
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Кнопка для управления тарифом */}
                    <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        onClick={() => navigate('/tariffs')}
                        variant="primary"
                        size="sm"
                        fullWidth
                      >
                        Управление подпиской
                      </Button>
                    </div>
                  </div>
                </div>
            )}
        </div>
      </div>

      {/* Диалог добавления участника команды */}
      {showAddMemberDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Добавить участника
                </h3>
                <button
                  onClick={() => {
                    setShowAddMemberDialog(false);
                    setNewMemberNickname('');
                    setUserExists(null);
                    setAddMemberError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть диалог"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Никнейм пользователя
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={newMemberNickname}
                    onChange={(e) => {
                      setNewMemberNickname(e.target.value);
                      checkUserExists(e.target.value);
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white pr-10 ${
                      checkingUser
                        ? 'border-blue-300 dark:border-blue-600'
                        : userExists === true 
                        ? 'border-green-300 dark:border-green-600' 
                        : userExists === false 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Введите никнейм"
                    disabled={addingMember}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {checkingUser ? (
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : userExists === true ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : userExists === false ? (
                      <UserX className="w-4 h-4 text-red-500" />
                    ) : null}
                  </div>
                </div>
                
                
                {userExists === false && (
                  <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400">
                    <UserX className="w-4 h-4 mr-2" />
                    Пользователь не найден
                  </div>
                )}
                
                {addMemberError && (
                  <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    {addMemberError}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowAddMemberDialog(false);
                    setNewMemberNickname('');
                    setUserExists(null);
                    setAddMemberError(null);
                  }}
                  disabled={addingMember}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium disabled:opacity-50"
                >
                  Отмена
                </button>
                <button
                  onClick={handleAddMember}
                  disabled={!newMemberNickname.trim() || addingMember || !userExists}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center"
                >
                  {addingMember ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    'Добавить'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Диалог подтверждения выхода */}
      {showLogoutDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mr-4">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Подтверждение выхода
                  </h3>
                </div>
                <button
                  onClick={handleLogoutCancel}
                  className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть диалог"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Вы уверены, что хотите выйти из системы?
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleLogoutCancel}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно истории транзакций */}
      {showReferralHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  История реферальных транзакций
                </h3>
                <button
                  onClick={() => {
                    setShowReferralHistory(false);
                    setReferralHistory([]);
                    setLoadingReferralHistory(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingReferralHistory ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">Загрузка истории...</p>
                </div>
              ) : referralHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">История транзакций пуста</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {referralHistory.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(transaction.dateCreated).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {transaction.referralUserEmail && (
                          <div className="text-sm text-blue-600 dark:text-blue-400">
                            От: {transaction.referralUserEmail}
                          </div>
                        )}
                      </div>
                      <div className={`text-lg font-bold ${
                        transaction.type === 'Earning' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'Earning' ? '+' : '-'}{Math.abs(transaction.amount).toFixed(2)} ₽
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно заявки на профессиональную программу */}
      {showProfessionalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Заявка на профессиональную программу
                </h3>
                <button
                  onClick={() => {
                    setShowProfessionalModal(false);
                    setProfessionalApplicationError(null);
                    setDataProcessingConsent(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Номер телефона
                  </label>
                  <input
                    type="tel"
                    value={professionalForm.phoneNumber}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Убираем все символы кроме цифр и +
                      value = value.replace(/[^\d+]/g, '');
                      
                      // Если пользователь начал вводить с 8 или 7, заменяем на +7
                      if (value.startsWith('8')) {
                        value = '+7' + value.slice(1);
                      } else if (value.startsWith('7') && !value.startsWith('+7')) {
                        value = '+7' + value.slice(1);
                      } else if (!value.startsWith('+7') && value.length > 0 && !value.startsWith('+')) {
                        value = '+7' + value;
                      }
                      
                      // Ограничиваем длину до +7 и 10 цифр
                      if (value.startsWith('+7') && value.length > 12) {
                        value = value.slice(0, 12);
                      }
                      
                      setProfessionalForm(prev => ({ ...prev, phoneNumber: value }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="+7 (999) 123-45-67"
                    pattern="^\+7\d{10}$"
                    maxLength={12}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Расскажите о себе
                  </label>
                  <textarea
                    value={professionalForm.message}
                    onChange={(e) => setProfessionalForm(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white h-24 resize-none"
                    placeholder="Укажите ваш блог, канал, медиа-проект или другую деятельность..."
                    required
                  />
                </div>
                
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="dataProcessingConsent"
                    checked={dataProcessingConsent}
                    onChange={(e) => setDataProcessingConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="dataProcessingConsent" className="text-sm text-gray-700 dark:text-gray-300">
                    Я согласен на обработку персональных данных в соответствии с политикой конфиденциальности
                  </label>
                </div>
                
                {professionalApplicationError && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {professionalApplicationError}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowProfessionalModal(false);
                    setProfessionalApplicationError(null);
                    setDataProcessingConsent(false);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleProfessionalApplication}
                  disabled={!professionalForm.phoneNumber.trim() || !professionalForm.message.trim() || !dataProcessingConsent}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Отправить заявку
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно вывода средств */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Вывод средств
                </h3>
                <button
                  onClick={() => {
                    setShowWithdrawalModal(false);
                    setWithdrawalApplicationError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-300">
                  <strong>Доступно для вывода:</strong> {referralInfo?.balance.toFixed(2)} ₽
                </p>
                <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                  Минимальная сумма: {referralInfo?.minWithdrawalAmount} ₽
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Сумма к выводу (₽)
                  </label>
                  <input
                    type="number"
                    min={referralInfo?.minWithdrawalAmount || 3000}
                    max={referralInfo?.balance || 0}
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: parseInt(e.target.value) || 3000 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  После отправки заявки мы свяжемся с вами для уточнения реквизитов.
                </div>
                
                {withdrawalApplicationError && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {withdrawalApplicationError}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowWithdrawalModal(false);
                    setWithdrawalApplicationError(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleWithdrawal}
                  disabled={withdrawalForm.amount < (referralInfo?.minWithdrawalAmount || 3000) || withdrawalForm.amount > (referralInfo?.balance || 0)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  Создать заявку
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Диалог удаления участника команды */}
      {showRemoveMemberDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Удалить участника
                </h3>
                <button
                  onClick={() => {
                    setShowRemoveMemberDialog(false);
                    setMemberToRemove(null);
                    setMemberRemovalError(null);
                    setMemberWithError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Закрыть диалог"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Вы уверены, что хотите удалить участника "{memberToRemove?.nickname}"?
              </p>
              
              {memberRemovalError && (
                <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {memberRemovalError}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRemoveMemberDialog(false);
                    setMemberToRemove(null);
                    setMemberRemovalError(null);
                    setMemberWithError(null);
                  }}
                  disabled={removingMember}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors font-medium disabled:opacity-50"
                >
                  Отмена
                </button>
                <button
                  onClick={handleRemoveMemberConfirm}
                  disabled={removingMember || !memberToRemove || memberRemovalError !== null}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {removingMember ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    'Удалить'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div></div>
  );
}

export default Profile;
